export const LOCAL_API_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "[::1]",
]);

/**
 * App hostname → same-site API origin. Auth cookies are host-only /
 * SameSite=Lax, so a page on neuroscale.ai cannot send them to
 * platform-api.arbi.ai. Keep the canonical NEXT_PUBLIC_API_URL pointing at
 * platform-api.arbi.ai for app.arbi.ai.
 */
export const ORIGIN_API_URLS: Record<string, string> = {
  "arbi.neuroscale.ai": "https://arbi-api.neuroscale.ai",
};

type BrowserLocation = Pick<Location, "hostname">;

function trimTrailingSlash(value: string): string {
  return value.replace(/\/$/, "");
}

function getBrowserLocation(): BrowserLocation | undefined {
  return typeof window === "undefined" ? undefined : window.location;
}

function shouldUsePageHost(
  apiHost: string,
  location: BrowserLocation | undefined,
  nodeEnv = process.env.NODE_ENV,
): location is BrowserLocation {
  if (nodeEnv === "production") return false;
  if (!location) return false;
  if (LOCAL_API_HOSTS.has(location.hostname)) return false;
  return LOCAL_API_HOSTS.has(apiHost);
}

export function apiUrlForPageHost(pageHostname: string | undefined): string | undefined {
  if (!pageHostname) return undefined;
  return ORIGIN_API_URLS[pageHostname];
}

export function getApiBaseUrl(
  configuredBaseUrl = process.env.NEXT_PUBLIC_API_URL ?? "",
  location = getBrowserLocation(),
  nodeEnv = process.env.NODE_ENV,
): string {
  const mapped = apiUrlForPageHost(location?.hostname);
  if (mapped) return trimTrailingSlash(mapped);

  const fallbackBaseUrl =
    configuredBaseUrl || (nodeEnv === "production" ? "" : "http://localhost:8000");

  try {
    const url = new URL(fallbackBaseUrl);
    if (shouldUsePageHost(url.hostname, location, nodeEnv)) {
      url.hostname = location.hostname;
    }
    return trimTrailingSlash(url.toString());
  } catch {
    return trimTrailingSlash(fallbackBaseUrl);
  }
}

export function getWebSocketApiBaseUrl(): string {
  return getApiBaseUrl().replace(/^http/, "ws");
}
